import { Injectable } from 'angular-ts-decorators';

import {
  defer,
  Observable,
  ConnectableObservable,
  Subject,
  merge,
  of,
  EMPTY,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mapTo,
  publishReplay,
  repeat,
  retry,
  share,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeWhile,
} from 'rxjs/operators';
import { isUndefined } from 'lodash';

import {
  PayNlPollReponse,
  PayNlRequests,
  PayNlStartReponse,
} from '@shared/app/scripts/services/pay-nl-requests';
import { PinProvider } from '@classes/pin-provider';
import { CurrentSession } from '@shared/app/scripts/services/current-session';

const PAY_DOT_RETURN_ID = 2351;

/**
 * Service that exposes connection to pay.nl payment provider.
 */
@Injectable('payNl')
export class PayNl implements PinProvider {
  private pollFailRetry = 3;
  private pollInterval = 500;

  private pollSubject = new Subject<Observable<PayNlPollReponse>>();
  private transactionStartedSubject = new Subject<
    Observable<PayNlStartReponse>
  >();
  private transactionStartedResponse$ = this.transactionStartedSubject.pipe(
    switchMap((response$) => response$),
  );
  private transactionStartedSuccessfulResponse$ =
    this.transactionStartedResponse$.pipe(
      catchError(() => EMPTY),
      filter((response) => !isUndefined(response?.terminal?.statusUrl)),
      publishReplay(1),
    );
  public print$ = EMPTY;
  public hasCcv$ = new Subject<boolean>();

  private transactionFailedStart$ = this.transactionStartedResponse$.pipe(
    catchError(() => EMPTY),
    map((response) => response?.terminal?.statusUrl),
    filter((statusUrl) => isUndefined(statusUrl)),
  );
  public transactionRejectedStart$ = this.transactionStartedResponse$.pipe(
    // remove all values
    filter(() => false),
    catchError((error) => of(error)),
  );
  private pollResponse$ = this.pollSubject.pipe(
    switchMap((poll$) => poll$),
    share(),
  );
  private finish$ = this.pollResponse$.pipe(
    catchError(() => EMPTY),
    filter((response) => {
      return response?.status === 'final';
    }),
  );
  private rejectedPolling$ = this.pollResponse$.pipe(
    filter(() => false),
    catchError((error) => of(error)),
  );

  constructor(
    private $q: ng.IQService,
    private PromiseConstructor,
    private currentSession: CurrentSession,
    private payNlRequests: PayNlRequests,
  ) {
    'ngInject';

    (<ConnectableObservable<boolean>>this.inProgress$).connect();
    (<ConnectableObservable<PayNlStartReponse>>(
      this.transactionStartedSuccessfulResponse$
    )).connect();
  }

  /**
   * Stream that emits true when payment starts & false when it stops
   */
  inProgress$: Observable<boolean> = merge(
    this.transactionStartedSubject.pipe(
      catchError(() => EMPTY),
      mapTo(true),
      shareReplay(),
    ),
    this.transactionFailedStart$.pipe(mapTo(false)),
    this.transactionRejectedStart$.pipe(mapTo(false)),
    this.finish$.pipe(mapTo(false)),
    this.rejectedPolling$.pipe(mapTo(false)),
  ).pipe(startWith(false), publishReplay(1));

  /**
   * Stream of card provider being used. Uses cardbrandlabelname +
   * cardbrandidentifier, which is different format then used by payPlaza
   * (application)
   */
  appName$: Observable<string> = this.pollResponse$.pipe(
    // ignore errors
    catchError(() => EMPTY),
    // ignore failed starts
    filter((response) => response?.approved === '1'),
    map(
      (response) =>
        `${response.cardbrandlabelname} (${response.cardbrandidentifier})`,
    ),
  );

  /**
   * Stream of info about progress
   */
  update$: Observable<string>;

  /**
   * Stream of transactionId
   */
  transactionId$: Observable<string> = this.transactionStartedResponse$.pipe(
    // ignore errors
    catchError(() => EMPTY),
    // ignore failed starts
    filter((response) => !isUndefined(response?.transaction?.transactionId)),
    map((response) => response.transaction.transactionId),
  );

  /**
   * Dummy implementation for {@link PinProvider#init}
   */
  init() {
    // as soon as we pull out pollSubject & startSubject to separate service,
    // streams set up will need to be moved here so we can mock stuff
  }

  /**
   * Implements method defined in {@link PinProvider#startTransaction}
   */
  startTransaction(
    transaction: {
      buffer_id: string;
      nr: string;
      customer?: { email?: string; telephone?: string };
    },
    amount: number,
  ): ng.IPromise<string> {
    const startTransaction$ = defer(() => {
      if (amount >= 0) {
        return this.payNlRequests.start(
          amount,
          transaction.customer?.email,
          transaction.customer?.telephone,
          this.currentSession.tenantId,
          transaction.nr,
          transaction.buffer_id,
        );
      }

      return this.payNlRequests.start(
        -amount,
        transaction.customer?.email,
        transaction.customer?.telephone,
        this.currentSession.tenantId,
        transaction.nr,
        transaction.buffer_id,
        PAY_DOT_RETURN_ID,
      );
    }).pipe(
      // consider retrying
      shareReplay(1),
    );

    this.transactionStartedSubject.next(startTransaction$);

    const query$ = startTransaction$.pipe(
      switchMap((result) => {
        if (result?.terminal?.statusUrl) {
          // Check for ccv in status URL immediately
          this.hasCcv$.next(result.terminal.statusUrl.includes('ccv'));

          // TODO find a way of achieve the same thing without nested stream creation
          const poll$ = defer(() =>
            this.payNlRequests.poll(result.terminal.statusUrl),
          ).pipe(
            retry(this.pollFailRetry),
            repeat(),
            share(),
            takeWhile((response) => response?.status !== 'final', true),
          );

          this.pollSubject.next(poll$);

          return poll$;
        } else {
          return EMPTY;
        }
      }),
    );

    const result$ = query$.pipe(
      filter((response) => response.status === 'final'),
      take(1),
    );

    return result$
      .toPromise(<PromiseConstructorLike>this.PromiseConstructor)
      .then((result) => {
        if (result?.approved === '1') {
          return atob(result.ticket);
        }

        return this.$q.reject(`${result.incidentcodetext}
------------------------------------------------
`);
      });
  }

  /**
   * Implements method defined in {@link PinProvider#abortTransaction}
   */
  abortTransaction(): void {
    this.inProgress$
      .pipe(
        take(1),
        filter((inProgress) => inProgress === true),
        switchMap(() => this.transactionStartedSuccessfulResponse$),
        take(1),
      )
      .subscribe((value) => {
        if (value.terminal.cancelUrl) {
          this.hasCcv$.next(value.terminal.cancelUrl.includes('ccv'));
          this.payNlRequests.poll(value.terminal.cancelUrl);
        } else if (value.terminal.statusUrl) {
          // hacky! but pay.nl seems to forget cancelUrl sometimes
          const cancelUrl = value.terminal.statusUrl.replace(
            '/api/status',
            '/api/cancel',
          );
          this.hasCcv$.next(cancelUrl.includes('ccv'));
          this.payNlRequests.poll(cancelUrl);
        }
      });
  }

  /**
   * Implements method defined in {@link PinProvider#checkServer}
   */
  checkServer(
    transaction: any,
  ): ng.IPromise<{ pickedUpTransactionPromise: ng.IPromise<string> }> {
    throw new Error(transaction);
  }

  /**
   * Dummy implementation.
   */
  reprintPINReceipt() {}
}
